import { format, parseISO } from 'date-fns';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import {
  Control,
  Controller,
  FieldError,
  UseFormSetValue,
  useFormState,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';

import { ShipmentSchema } from '../../utils/schema';

type FormFieldProps<
  C extends
    | typeof AutoComplete
    | typeof Calendar
    | typeof InputNumber
    | typeof Dropdown
    | typeof InputText,
> = {
  label: string;
  name: keyof ShipmentSchema;
  control: Control<ShipmentSchema>;
  component: C;
  setValue?: UseFormSetValue<ShipmentSchema>;
  props?: React.ComponentPropsWithoutRef<C>;
};

export const FormField = <
  C extends
    | typeof AutoComplete
    | typeof Calendar
    | typeof InputNumber
    | typeof Dropdown
    | typeof InputText,
>({
  label,
  name,
  control,
  component,
  setValue,
  props,
}: FormFieldProps<C>) => {
  const Component = component as React.ComponentType<any>;
  const { errors } = useFormState({ control, name });
  const fieldError = (errors[name] as FieldError | undefined)?.message;

  return (
    <div className="label-input-group">
      <label htmlFor={name} id={`${name}-label`}>
        {label}
      </label>
      <Controller
        name={name}
        control={control}
        disabled={props?.disabled}
        render={({ field: { ref, ...field }, fieldState }) => {
          const value = field.value ?? '';

          if (Component === Dropdown) {
            return (
              <Dropdown
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<typeof Dropdown>)}
                {...field}
                inputRef={ref}
                className={fieldState.invalid ? 'p-invalid' : ''}
              />
            );
          }

          if (Component === AutoComplete) {
            return (
              <AutoComplete
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<
                  typeof AutoComplete
                >)}
                {...field}
                inputRef={ref}
                className={fieldState.invalid ? 'p-invalid' : ''}
                onChange={(e: any) => {
                  field.onChange(e.value);

                  if (!setValue) {
                    return;
                  }

                  switch (field.name) {
                    case 'owner':
                      setValue('owner', e.value);
                      setValue('ownerId', e.value?.id);
                      break;
                    case 'packedBy':
                      setValue('packedById', e.value?.id ?? null);
                      break;
                    case 'shipFromSite':
                      setValue('shipFromSiteId', e.value?.id);
                      break;
                    case 'shipTo':
                      setValue('shipTo', e.value);
                      setValue('shipToId', e.value?.id ?? null);
                      break;
                  }
                }}
                onSelect={(e: any) => {
                  field.onChange(e.value);

                  if (!setValue) {
                    return;
                  }

                  switch (field.name) {
                    case 'owner':
                      setValue('owner', e.value);
                      setValue('ownerId', e.value?.id);
                      break;
                    case 'packedBy':
                      setValue('packedById', e.value?.id ?? null);
                      break;
                    case 'shipFromSite':
                      setValue('shipFromSiteId', e.value?.id);
                      break;
                    case 'shipTo':
                      setValue('shipTo', e.value);
                      setValue('shipToId', e.value?.id ?? null);
                      break;
                  }
                }}
              />
            );
          }

          if (Component === Calendar) {
            const calendarProps = props as React.ComponentPropsWithoutRef<
              typeof Calendar
            >;
            return (
              <Calendar
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...calendarProps}
                {...field}
                inputRef={ref}
                value={
                  value
                    ? calendarProps.parseDateTime
                      ? calendarProps.parseDateTime(value.toString())
                      : new Date(value.toString())
                    : null
                }
                className={fieldState.invalid ? 'p-invalid' : ''}
                onChange={(e) => {
                  if (e.value instanceof Date || e.value === null) {
                    field.onChange(
                      e.value instanceof Date
                        ? calendarProps.formatDateTime
                          ? calendarProps.formatDateTime(e.value)
                          : e.value.toISOString()
                        : null,
                    );
                  }
                }}
              />
            );
          }

          if (Component === InputNumber) {
            return (
              <InputNumber
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<
                  typeof InputNumber
                >)}
                {...field}
                inputRef={ref}
                value={field.value as number}
                onValueChange={(e: InputNumberValueChangeEvent) => {
                  field.onChange(e.value);
                }}
                className={fieldState.invalid ? 'p-invalid' : ''}
              />
            );
          }

          return (
            <Component
              id={field.name}
              aria-labelledby={`${field.name}-label`}
              {...props}
              {...field}
              value={value}
              className={fieldState.invalid ? 'p-invalid' : ''}
            />
          );
        }}
      />
      {fieldError && <small className="p-error">{fieldError}</small>}
    </div>
  );
};

export const AccountField = ({
  control,
  setValue,
  value,
  shipmentInProgress,
  filteredAccounts,
  searchAccount,
  setFilteredAccounts,
  minLength,
  selectionLimit,
}: {
  control: Control<ShipmentSchema>;
  setValue: UseFormSetValue<ShipmentSchema>;
  value: { id: string; name: string } | null;
  shipmentInProgress?: boolean;
  filteredAccounts: any;
  searchAccount: any;
  setFilteredAccounts: any;
  minLength: number;
  selectionLimit: number;
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      label={t('account.account')}
      name="owner"
      control={control}
      setValue={setValue}
      component={AutoComplete}
      props={{
        field: 'name',
        suggestions: filteredAccounts,
        minLength,
        selectionLimit,
        completeMethod: searchAccount,
        onDropdownClick: () => setFilteredAccounts(undefined),
        dropdown: true,
        value,
        autoFocus: true,
        disabled: shipmentInProgress,
      }}
    />
  );
};

export const NeedByDateField = ({
  control,
  shipmentInProgress,
}: {
  control: Control<ShipmentSchema>;
  shipmentInProgress?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      label={t('shipping.needByDate')}
      name="needByDate"
      control={control}
      component={Calendar}
      props={{
        hourFormat: '24',
        showIcon: true,
        disabled: shipmentInProgress,
        parseDateTime(text) {
          const datePart = text.slice(0, 10);
          return parseISO(datePart);
        },
        formatDateTime(date) {
          return format(date, 'yyyy-MM-dd');
        },
      }}
    />
  );
};

export const OriginalNeedByDateField = ({
  control,
  shipmentInProgress,
}: {
  control: Control<ShipmentSchema>;
  shipmentInProgress?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      label={t('shipping.originalNeedByDate')}
      name="originalNeedByDate"
      control={control}
      component={Calendar}
      props={{
        showIcon: true,
        disabled: shipmentInProgress,
      }}
    />
  );
};
